import React, { CSSProperties, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Button, Input, Select, Steps, message, theme } from "antd";
import { useSubscribe } from "../../api/subscribtion";
import { useGetUserByEmail, useGetUserByUserName } from "../../api/users";
import ReCAPTCHA from "react-google-recaptcha";
import { subscribeSchema } from "../../schemas/formSchema";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import z from "zod";
import { FormField } from "../../components/form/FormField";

const wrapper: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  // gap: 10,
  alignItems: "center",
  borderBottom: "1px solid #E9EDF4 ",
};

const label: CSSProperties = {
  color: "#8A979D",
  fontFamily: "Poppins",
  fontSize: 16,
};

const value: CSSProperties = {
  fontFamily: "Poppins",
  fontSize: 16,
};

type SubscribeFormData = z.infer<typeof subscribeSchema>;

function SubscribePage() {
  const navigate = useNavigate();
  const { orgType } = useParams();
  const { mutateAsync: subscribe, isPending: subscribing } = useSubscribe();
  const [recaptchaValue, setRecaptchaValue] = useState(null);
  const [type, setType] = useState<string | undefined>();
  const [orgTypeDisabled, setOrgTypeDisabled] = useState(false);
  const [userName, setUserName] = useState<string | undefined>();
  const [email, setEmail] = useState<string | undefined>();

  const { data: userNameData, refetch: fetchUserName } =
    useGetUserByUserName(userName);
  const { data: userEmailData, refetch: fetchUserEmail } =
    useGetUserByEmail(email);

  const {
    control,
    handleSubmit,
    getValues,
    watch,
    formState: { errors, isValid, isDirty },
  } = useForm<SubscribeFormData>({
    resolver: zodResolver(subscribeSchema),
    mode: "onBlur",
    defaultValues: {
      companyName: "",
      shortName: "",
      companyEmail: "",
      email: "",
      companyPhoneNumber: "",
      region: "",
      city: "",
      kifleketema: "",
      woreda: "",
      kebele: "",
      houseNumber: "",
      phoneNumber: "",
      fullName: "",
      username: "",
      type: "",
    },
  });

  const requiredFields = [
    "companyName",
    "shortName",
    "companyEmail",
    "companyPhoneNumber",
    "region",
    "city",
    "woreda",
    "kebele",
    "houseNumber",
  ];

  const fieldValues = watch(requiredFields as any);
  const hasEmptyFields = fieldValues.some(
    (value: any) => !value?.toString().trim()
  );

  useEffect(() => {
    if (orgType) {
      const _type = orgType.toUpperCase();
      if (
        _type === "MICRO-FINANCE" ||
        _type === "INSURANCE-COMPANY" ||
        _type === "OFF_TAKERS" ||
        _type === "MULTI_PURPOSE_COOPERATIVE" ||
        _type === "MERCHANT" ||
        _type === "MACHINE_RENTER" ||
        _type === "SHEGA_FRE" || 
        _type === "TRANSPORTERS"
      )
        setType(_type);
      setOrgTypeDisabled(true);
    }
  }, [orgType]);

  const steps = [
    {
      title: "Organization",
      content: (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 20,
            alignSelf: "center",
            width: "100%",
            height: 100,
          }}
        >
          <div
            style={{
              display: "flex",
              fontFamily: "Poppins",
              fontWeight: 500,
              fontSize: 20,
            }}
          >
            Company info
          </div>

          {type == "MULTI_PURPOSE_COOPERATIVE" && (
            <FormField
              type="select"
              name="type"
              control={control}
              label="Company Type"
              placeholder="Select company Type"
              options={[
                { label: "MERCHANT", value: "MERCHANT" },
                { label: "MPC", value: "MULTI_PURPOSE_COOPERATIVE" },
              ].map((type: { label: string; value: string }) => {
                return { label: type.label, value: type.value };
              })}
              errors={errors}
            />
          )}

          <div className="flex flex-col sm:flex-row gap-5">
            <FormField
              type="input"
              name="companyName"
              control={control}
              label="Company Name"
              placeholder="Enter company name"
              errors={errors}
            />
            <FormField
              type="input"
              name="shortName"
              control={control}
              label="Short Name"
              placeholder="Enter short name"
              errors={errors}
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-5">
            <FormField
              type="input"
              name="companyEmail"
              control={control}
              label="Email"
              placeholder="Enter company email"
              errors={errors}
            />
            <FormField
              type="input"
              name="companyPhoneNumber"
              control={control}
              label="Phone Number"
              placeholder="Enter phone number"
              errors={errors}
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-5">
            <FormField
              type="input"
              name="region"
              control={control}
              label="Region"
              placeholder="Region"
              errors={errors}
            />
            <FormField
              type="input"
              name="city"
              control={control}
              label="City"
              placeholder="City"
              errors={errors}
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-5">
            <FormField
              name="kifleketema"
              control={control}
              label="Kifle Ketema"
              placeholder="Kifle ketema"
              errors={errors}
            />
            <FormField
              name="woreda"
              control={control}
              label="Woreda"
              placeholder="Woreda"
              errors={errors}
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-5">
            <FormField
              name="kebele"
              control={control}
              label="Kebele"
              placeholder="Kebele"
              errors={errors}
            />
            <FormField
              name="houseNumber"
              control={control}
              label="House Number"
              placeholder="House number"
              errors={errors}
            />
          </div>
        </div>
      ),
    },
    {
      title: "Contact person",
      content: (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 20,
            alignSelf: "center",
            height: "300px",
          }}
        >
          <div
            style={{
              display: "flex",
              fontFamily: "Poppins",
              fontWeight: 500,
              fontSize: 20,
            }}
          >
            Contact person info
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              gap: 20,
              width: "100%",
              height: "300px",
            }}
          >
            <div className="flex flex-col sm:flex-row gap-5">
              <FormField
                name="username"
                control={control}
                label="User Name"
                placeholder="Enter user name"
                errors={errors}
              />
              <FormField
                name="fullName"
                control={control}
                label="Full Name"
                placeholder="Enter full name"
                errors={errors}
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-5">
              <FormField
                name="email"
                control={control}
                label="Email"
                placeholder="Enter email"
                errors={errors}
              />
              <FormField
                name="phoneNumber"
                control={control}
                label="Phone Number"
                placeholder="Enter phone number"
                errors={errors}
              />
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Review",
      content: (
        <div className="flex flex-col sm:flex-row gap-10 justify-center self-center">
          <div className="w-full sm:w-1/2 flex flex-col gap-3">
            <div
              style={{
                display: "flex",
                fontFamily: "Poppins",
                fontWeight: 500,
                fontSize: 20,
                backgroundColor: "#E9EDF4",
                padding: 6,
                borderRadius: 8,
              }}
            >
              Company Info
            </div>

            <div style={wrapper}>
              <div style={label}>Company type:</div>
              <div style={value}>{getValues("type")}</div>
            </div>
            <div style={wrapper}>
              <div style={label}>Company name:</div>
              <div style={value}>{getValues("companyName")}</div>
            </div>

            <div style={wrapper}>
              <div style={label}>Short name:</div>
              <div style={value}>{getValues("shortName")}</div>
            </div>

            <div style={wrapper}>
              <div style={label}>Email:</div>
              <div style={value}>{getValues("companyEmail")}</div>
            </div>

            <div style={wrapper}>
              <div style={label}>Phone number:</div>
              <div style={value}>{getValues("companyPhoneNumber")}</div>
            </div>

            <div style={wrapper}>
              <div style={label}>Region:</div>
              <div style={value}>{getValues("region")}</div>
            </div>

            <div style={wrapper}>
              <div style={label}>City:</div>
              <div style={value}>{getValues("city")}</div>
            </div>

            <div style={wrapper}>
              <div style={label}>Kifle ketema:</div>
              <div style={value}>{getValues("kifleketema")}</div>
            </div>

            <div style={wrapper}>
              <div style={label}>Woreda:</div>
              <div style={value}>{getValues("woreda")}</div>
            </div>

            <div style={wrapper}>
              <div style={label}>Kebele:</div>
              <div style={value}>{getValues("kebele")}</div>
            </div>

            <div style={wrapper}>
              <div style={label}>House number:</div>
              <div style={value}>{getValues("houseNumber")}</div>
            </div>
          </div>

          <div className="w-full sm:w-1/2 flex flex-col gap-3">
            <div
              style={{
                display: "flex",
                fontFamily: "Poppins",
                fontWeight: 500,
                fontSize: 20,
                backgroundColor: "#E9EDF4",
                padding: 6,
                borderRadius: 8,
              }}
            >
              Contact Person Info
            </div>

            <div style={wrapper}>
              <div style={label}>User name:</div>
              <div style={value}>{getValues("username")}</div>
            </div>

            <div style={wrapper}>
              <div style={label}>Full name:</div>
              <div style={value}>{getValues("fullName")}</div>
            </div>

            <div style={wrapper}>
              <div style={label}>Email:</div>
              <div style={value}>{getValues("email")}</div>
            </div>

            <div style={wrapper}>
              <div style={label}>Phone number:</div>
              <div style={value}>{getValues("phoneNumber")}</div>
            </div>

            <div className="w-full h-[200px] flex justify-center items-center">
              <ReCAPTCHA
                style={{ marginTop: 20, alignItems: "center" }}
                sitekey={"6LdZRp8qAAAAAPIwhObOgWqvZidvBwilMLGPK_hw"}
                onChange={(token: any) => setRecaptchaValue(token)}
              />
            </div>
          </div>
        </div>
      ),
    },
  ];

  const { token } = theme.useToken();
  const [current, setCurrent] = useState(0);
  const stepContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    stepContentRef.current?.scrollTo({ top: 0, behavior: "auto" });
  }, [current]);

  const next = () => {
    setCurrent(current + 1);
  };

  const prev = () => {
    setCurrent(current - 1);
  };

  const items = steps.map((item) => ({ key: item.title, title: item.title }));

  const contentStyle: React.CSSProperties = {
    // lineHeight: '500px',
    minHeight: 450,
    textAlign: "center",
    color: "black",
    backgroundColor: "white",
    borderTopLeftRadius: token.borderRadiusLG,
    borderTopRightRadius: token.borderRadiusLG,
    marginTop: 16,
    padding: 20,
  };

  const onSubmit = async () => {
    const address = JSON.stringify({
      region: getValues("region"),
      kifleKetema: getValues("kifleketema"),
      woreda: getValues("woreda"),
      kebele: getValues("kebele"),
    });

    const formData = {
      name: getValues("companyName"),
      email: getValues("companyEmail"),
      phoneNumber: getValues("companyPhoneNumber"),
      // type: getValues("type"),
      type: type == "MULTI_PURPOSE_COOPERATIVE" ? getValues("type") : type,
      address: address,
      contactPerson: {
        fullName: getValues("fullName"),
        username: getValues("username"),
        email: getValues("email"),
        phoneNumber: getValues("phoneNumber"),
      },
    };

    try {
      const result = await subscribe(formData as any);

      if (result && result.data && result.data.status === "success") {
        message.success("Subscribed successfully.");
        navigate("/");
      } else {
        message.error("Operation failed.");
      }
    } catch (err: any) {
      message.error("Operation failed.");
    }
  };

  return (
    <div
      className="p-0"
      style={{
        width: "100%",
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        paddingTop: 32,
        paddingBottom: 48,
        overflow: "auto",
      }}
    >
      <div className="shadow-2xl p-4 rounded-xl w-4/5 sm:w-1/2">
        <Steps current={current} items={items} />
        <div
          ref={stepContentRef}
          style={contentStyle}
          className="overflow-y-scroll"
        >
          {steps[current].content}
        </div>
        <div className="mt-6 bg-white flex gap-6 justify-center items-center py-3">
          {current > 0 && (
            <Button style={{ margin: "0 8px" }} onClick={() => prev()}>
              Previous
            </Button>
          )}
          {current < steps.length - 1 && (
            <Button
              type="primary"
              onClick={() => next()}
              disabled={hasEmptyFields}
            >
              Next
            </Button>
          )}
          {current === steps.length - 1 && (
            <Button
              type="primary"
              onClick={handleSubmit(onSubmit)}
              disabled={!isValid || !recaptchaValue}
            >
              {subscribing ? "Subscribing" : "Done"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default SubscribePage;
